import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  styled,
  CheckboxProps,
} from '@mui/material';
import { useId, ReactNode } from 'react';
import { theme } from '~/shared/styles/theme';

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: ReactNode;
}

export const ConsentCheckbox = <T extends FieldValues>({
  name,
  control,
  label,
}: Props<T>) => {
  const id = useId();
  const labelId = `${name}-${id}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <StyledFormControlLabel
          control={
            <StyledCheckbox
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              data-testid={`checkbox-${name}`}
              slotProps={{
                input: {
                  'aria-labelledby': labelId,
                },
              }}
            />
          }
          label={<LabelContent id={labelId}>{label}</LabelContent>}
        />
      )}
    />
  );
};

const StyledFormControlLabel = styled(FormControlLabel)({
  width: '100%',
  margin: 0,
  padding: 0,
  backgroundColor: theme.palette.custom.cream,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.custom.lightGrayBrown}`,
  height: '84px',
});

const StyledCheckbox = styled(Checkbox)<CheckboxProps>({
  color: theme.palette.custom.lightGrayBrown,
  marginRight: theme.spacing(2),
  '&.Mui-checked': {
    color: theme.palette.custom.green,
  },
  '& .MuiSvgIcon-root': {
    width: '36px',
    height: '36px',
  },
});

const LabelContent = styled('span')({
  color: theme.palette.custom.mediumBrown,
  fontSize: '15px',
  lineHeight: 1.4,
  '& a': {
    color: theme.palette.custom.mediumBrown,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});
